import {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useAuth} from './AuthContext';
import {extrairMe, temVisao} from './portalAccess';
import {getToken} from '../utils/session';
import {getMe} from '../../features/Usuarios/UsersService';
import {environment} from '../../config/environment';

// Sem o provedor, nenhuma área com visão abre.
const PortalAccessContext = createContext({
    carregando: false,
    erro: false,
    me: null,
    podeVer: (visao) => !visao,
});

/**
 * Lê o `/me` uma vez por sessão (por token) e guarda: as visões do portal decidem o menu e as rotas.
 * No modo de demonstração (`disableAuth`) tudo fica aberto, como o login.
 */
export function PortalAccessProvider({children}) {
    const {authenticated, userLogged} = useAuth() || {};
    const token = authenticated ? getToken(userLogged) : null;
    const [estado, setEstado] = useState({token: null, carregando: false, erro: false, me: null});

    useEffect(() => {
        if (environment.disableAuth || !token) {
            setEstado({token: null, carregando: false, erro: false, me: null});
            return undefined;
        }

        let cancelado = false;
        setEstado({token, carregando: true, erro: false, me: null});
        getMe()
            .then((response) => {
                if (!cancelado) setEstado({token, carregando: false, erro: false, me: extrairMe(response)});
            })
            .catch(() => {
                if (!cancelado) setEstado({token, carregando: false, erro: true, me: null});
            });

        return () => {
            cancelado = true;
        };
    }, [token]);

    // Logo depois do login o token mudou e o efeito ainda não rodou: já conta como carregando.
    const carregando = !environment.disableAuth && Boolean(token) && (estado.carregando || estado.token !== token);

    const podeVer = useCallback((visao) => {
        if (environment.disableAuth) return true;
        return temVisao(estado.me, visao);
    }, [estado.me]);

    const value = useMemo(() => ({
        carregando,
        erro: estado.erro,
        me: estado.me,
        podeVer,
    }), [carregando, estado.erro, estado.me, podeVer]);

    return <PortalAccessContext.Provider value={value}>{children}</PortalAccessContext.Provider>;
}

export const usePortalAccess = () => useContext(PortalAccessContext);
